/**
 * Yönetim panelinin ayrı bir alan adında olup olmadığını çözer.
 *
 * Kurulum panelin adresini adres defterinden atayabilir (bkz.
 * backend/services/site_address.py). İstemci bu adresi **öğrenmez**: sunucu
 * yalnızca üç bayrak döndürür — ayrım açık mı, kilit yürürlükte mi ve
 * "burası panel adresi mi". Panelin adresi portalın paketine hiç geçmez.
 *
 * Üç mod vardır:
 *
 *   off    Ayrım kapalı (varsayılan). Panel bugünkü gibi ana sitede,
 *          /admin yolunda açılır.
 *   panel  Burası panel adresi. Portal kabuğu hiç çizilmez; kök adres
 *          panele yönlenir.
 *   portal Ayrım açık ama burası ana site. Kilit yürürlükteyse /admin
 *          yolları hiç tanımlanmaz.
 *
 * Kilit **atamayla değil onayla** devreye girer: adres atandıktan sonra
 * yönetici o adresten girip kilidi onaylayana kadar panel iki adresten de
 * açılır. Bu bilinçlidir — ulaşılamayan bir adres yöneticiyi her yerden
 * dışarıda bırakmamalıdır (arayüzün de sunucunun da kuralı aynı).
 */

export const ADMIN_DOMAIN_OFF = 'off';
export const ADMIN_DOMAIN_PANEL = 'panel';
export const ADMIN_DOMAIN_PORTAL = 'portal';

export const adminDomainMode = (settings) => {
  if (!settings?.admin_domain_enabled) return ADMIN_DOMAIN_OFF;
  if (settings.admin_host) return ADMIN_DOMAIN_PANEL;
  return ADMIN_DOMAIN_PORTAL;
};

/** Bu adreste /admin rotaları tanımlansın mı? */
export const adminRoutesVisible = (settings) => {
  const mode = adminDomainMode(settings);
  if (mode === ADMIN_DOMAIN_OFF || mode === ADMIN_DOMAIN_PANEL) return true;
  // Ana site: yalnızca kilit henüz onaylanmadıysa panel burada da durur.
  return !settings?.admin_domain_locked;
};

/** Oyuncu portalının kabuğu bu adreste çizilsin mi? */
export const portalVisible = (settings) => (
  adminDomainMode(settings) !== ADMIN_DOMAIN_PANEL
);

/**
 * Portaldaki "Yönetim Paneli" kısayolu görünsün mü?
 *
 * Ölçü, bağlantının bu adreste **çalışıp çalışmadığıdır**: kilit yürürlükte
 * değilse panel ana siteden de açılır ve kısayol işe yarar; kilitlendiği anda
 * bağlantı hiçbir şey açmayacağı için çizilmez — adresi bilmediğimiz için
 * panele yönlendiremeyiz ve varlığını duyurmanın da anlamı yoktur.
 */
export const adminShortcutVisible = (settings) => adminRoutesVisible(settings);

const ADMIN_PATH = /^\/admin(?:\/|$)/;

/**
 * Bu adres gerçekten yönetim paneli mi?
 *
 * Yol tek başına ölçü değildir: panel ayrı bir alan adına kilitlendiğinde
 * ana sitedeki ``/admin`` panelin değil portalın 404 sayfasıdır. Panele özgü
 * istisnalar (özel kodun uygulanmaması, yakınlaştırma kilidinin kalkması,
 * tarayıcı korumalarının açılması, ``data-bhs-page="admin"``) o sayfaya
 * uygulansaydı ziyaretçi Betrun yerine özel kodu olmayan çıplak temayı görür
 * ve adresin sıradan bir tanımsız adres olmadığını anlardı.
 *
 * Ayarlar gelmeden karar "panel"dir (ayrım kapalı sayılır): yönetici oturumu
 * üçüncü taraf betiklere bir an bile açılmamalıdır.
 */
export const isPanelPath = (settings, pathname) => (
  ADMIN_PATH.test(String(pathname || '')) && adminRoutesVisible(settings)
);

/**
 * `public/index.html`'in ilk betikleri ayarları bilmez; ana sitede ``/admin``
 * yolunun sıradan bir portal sayfası olduğunu (kilit yürürlükte) buradan
 * okurlar. Anahtar ve değer index.html ile aynı kalmalıdır.
 */
export const ADMIN_PATH_IS_PORTAL_KEY = 'app:admin-path-portal-v1';

export const cacheAdminPathIsPortal = (
  settings,
  storage = typeof window !== 'undefined' ? window.localStorage : null,
) => {
  try {
    if (portalVisible(settings) && !adminRoutesVisible(settings)) {
      storage?.setItem(ADMIN_PATH_IS_PORTAL_KEY, '1');
    } else {
      storage?.removeItem(ADMIN_PATH_IS_PORTAL_KEY);
    }
  } catch {
    // Depolama kapalıysa özel kod yine ayarlar gelince uygulanır.
  }
};
